import { IBaseBusinessManageRepository } from '../interfaces/IBaseBusinessManageRepository';
import { INetworkResult } from '@/coreDomainsModule/networkResult';
import { IError } from '../interfaces/IError';
import { customAxiosInstance } from '../axios/axiosInstance';
import { axiosErrorHandler } from '../axios/axiosError';
import {
  CustomerNameFromMasterApi,
  SalesDestinationNameFromMasterApi,
  ErrorResponse,
  BaseBusinessPutRequest,
  BaseBusinessApi,
  SuccessResponse,
  BaseBusinessPostRequest,
  PostBaseBusinessResponse,
} from '../axios/generated/baseBusinessManage';
import { AxiosResponse } from 'axios';

/**
 * 関連取引のリポジトリ実装
 */
export class BaseBusinessManageRepository implements IBaseBusinessManageRepository {
  /**
   * 関連取引の作成
   * @param baseBusinessInfo - 作成する関連取引
   */
  async post(baseBusinessInfo: BaseBusinessPostRequest): Promise<INetworkResult<string, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const api = new BaseBusinessApi(undefined, undefined, axiosInstance);

    try {
      const apiResponse: AxiosResponse<PostBaseBusinessResponse, IError> = await api.baseBusinessPost(baseBusinessInfo);
      switch (apiResponse.status) {
        case 200:
          const result = apiResponse.data.result.baseBusinessId;
          return { status: { type: 'success', data: result } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }
  /**
   * 関連取引の編集
   * @param baseBusinessInfo - 編集する関連取引
   */
  async put(baseBusinessInfo: BaseBusinessPutRequest): Promise<INetworkResult<void, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const api = new BaseBusinessApi(undefined, undefined, axiosInstance);

    try {
      const apiResponse: AxiosResponse<SuccessResponse, IError> = await api.baseBusinessPut(baseBusinessInfo);
      switch (apiResponse.status) {
        case 200:
          return { status: { type: 'success', data: undefined } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }
  /**
   * 売上先番号から売上先名を取得
   * @param customerNumber - 売上先番号
   */
  async getCustomerName(customerNumber: string): Promise<INetworkResult<string | undefined, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const api = new CustomerNameFromMasterApi(undefined, undefined, axiosInstance);

    try {
      const apiResponse: AxiosResponse = await api.customerNameFromMasterGet(undefined, customerNumber);
      switch (apiResponse.status) {
        case 200:
          const result: string | undefined = apiResponse.data.result.customerNames?.[0];
          return { status: { type: 'success', data: result } };
        default:
          throw new Error('Unexpected response');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 売上先名から売上先番号を取得
   * @param customerName - 売上先名
   */
  async getCustomerNumber(customerName: string): Promise<INetworkResult<string | undefined, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const api = new CustomerNameFromMasterApi(undefined, undefined, axiosInstance);

    try {
      const apiResponse: AxiosResponse = await api.customerNameFromMasterGet(customerName);
      switch (apiResponse.status) {
        case 200:
          const result: string | undefined = apiResponse.data.result.customerNumbers?.[0];
          return { status: { type: 'success', data: result } };
        default:
          throw new Error('Unexpected response');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 送り先番号から送り先名を取得
   * @param salesDestinationNumber - 送り先番号
   */
  async getSalesDestinationName(salesDestinationNumber: string): Promise<INetworkResult<string | undefined, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const api = new SalesDestinationNameFromMasterApi(undefined, undefined, axiosInstance);

    try {
      const apiResponse: AxiosResponse = await api.salesDestinationNameFromMasterGet(undefined, salesDestinationNumber);
      switch (apiResponse.status) {
        case 200:
          const result: string | undefined = apiResponse.data.result.salesDestinationNames?.[0];
          return { status: { type: 'success', data: result } };
        default:
          throw new Error('Unexpected response');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 送り先名から送り先番号を取得
   * @param salesDestinationName - 送り先名
   */
  async getSalesDestinationNumber(salesDestinationName: string): Promise<INetworkResult<string | undefined, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const api = new SalesDestinationNameFromMasterApi(undefined, undefined, axiosInstance);

    try {
      const apiResponse: AxiosResponse = await api.salesDestinationNameFromMasterGet(salesDestinationName);
      switch (apiResponse.status) {
        case 200:
          const result: string | undefined = apiResponse.data.result.salesDestinationNumbers?.[0];
          return { status: { type: 'success', data: result } };
        default:
          throw new Error('Unexpected response');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }
}
